import { AxiosRequestConfig } from 'axios'
import { isHatchEnabledClient } from '../shared/hatch'
import { getAPIPath, requestGooseAPI, requestHatchAPI } from '../shared/request'
import type { APIBookingSearchByDaysClientDtoResponse, APIBookingSearchClientDtoResponse } from '../types/api'
import { CLIENT_BOOKING_SEARCH_DAYS_PATH, CLIENT_BOOKING_SEARCH_PATH } from '../types/constants'
import { TBookingSearchOptions } from '../types/general'
import { petsFromGroups, withHatchDiagnostics } from './diagnostics/hatch_diagnostics'

type Requester = <T>(options: AxiosRequestConfig) => Promise<T>

const _search = (
  requester: Requester,
  locationId: string,
  serviceTypeName: string,
  options: TBookingSearchOptions
): Promise<APIBookingSearchClientDtoResponse> => {
  return requester<APIBookingSearchClientDtoResponse>({
    url: getAPIPath(CLIENT_BOOKING_SEARCH_PATH, { locationId, serviceTypeName }),
    method: 'POST',
    params: {
      start: options.startDate,
      end: options.endDate,
      type: options.offersType,
      required: options.required?.toString(),
    },
    data: {
      petGroups: options.petGroups,
    },
  })
}

const _searchByDays = (
  requester: Requester,
  locationId: string,
  serviceTypeName: string,
  options: TBookingSearchOptions
): Promise<APIBookingSearchByDaysClientDtoResponse> => {
  const params = {
    start: options.startDate,
    end: options.endDate,
    type: options.offersType,
  } as Record<string, string>
  if (options.offerName) {
    params['name'] = options.offerName
  }

  return requester<APIBookingSearchByDaysClientDtoResponse>({
    url: getAPIPath(CLIENT_BOOKING_SEARCH_DAYS_PATH, { locationId, serviceTypeName }),
    method: 'POST',
    params,
    data: {
      petGroups: options.petGroups,
    },
  })
}

export const ClientBookingSearchService = {
  search: async (
    locationId: string,
    serviceTypeName: string,
    options: TBookingSearchOptions
  ): Promise<APIBookingSearchClientDtoResponse> => {
    const hatchEnabled = await isHatchEnabledClient(locationId)
    const requester = hatchEnabled ? requestHatchAPI : requestGooseAPI
    return withHatchDiagnostics(_search(requester, locationId, serviceTypeName, options), {
      location: locationId,
      serviceTypeName,
      apiType: 'client',
      endpoint: 'search',
      params: {
        type: options.offersType,
        required: options.required,
        startDate: options.startDate,
        endDate: options.endDate,
      },
      parentOffer: options.petGroups?.[0]?.parentOffer,
      pets: petsFromGroups(options.petGroups),
    })
  },

  searchByDays: async (
    locationId: string,
    serviceTypeName: string,
    options: TBookingSearchOptions
  ): Promise<APIBookingSearchByDaysClientDtoResponse> => {
    const hatchEnabled = await isHatchEnabledClient(locationId)
    const requester = hatchEnabled ? requestHatchAPI : requestGooseAPI
    return withHatchDiagnostics(_searchByDays(requester, locationId, serviceTypeName, options), {
      location: locationId,
      serviceTypeName,
      apiType: 'client',
      endpoint: 'searchByDays',
      params: { type: options.offersType, startDate: options.startDate, endDate: options.endDate },
      parentOffer: options.petGroups?.[0]?.parentOffer,
      pets: petsFromGroups(options.petGroups),
    })
  },
}
